/**
 * Stroke index alignment between hull-set scores and gate-force / arc RPC data.
 *
 * Gate force and rowing arc use `stroke_windows.stroke_index`, which is 1-based and built
 * from the same ordered `telemetry_stroke_metrics.timestamp` list as upload (see
 * strokeWindowService.buildStrokeWindows).
 */

import type { StrokeStabilityRecord } from '../../hooks/useStrokeStabilityData';

export type StrokeWindowRow = {
  stroke_index: number;
  window_start_ms: number;
  window_end_ms: number;
};

const STROKE_TIMESTAMP_MATCH_MS = 1000;

/**
 * Map hull-set scores to gate-force stroke_index via stroke_windows + stroke metrics order.
 * Windows share the same ordinal order as telemetry_stroke_metrics (see strokeWindowService).
 */
export function buildAllowedStrokeIndicesFromWindows(
  windows: StrokeWindowRow[],
  orderedTimestamps: number[],
  stabilityRows: Array<Pick<StrokeStabilityRecord, 'stroke_timestamp' | 'clean_for_analysis'>>,
  boatMotionMinTimestamp: number | null
): Set<number> {
  const indices = new Set<number>();
  if (!orderedTimestamps.length) return indices;

  const alignedTimestamps = alignStrokeMetricTimestamps(orderedTimestamps, boatMotionMinTimestamp);

  for (const row of stabilityRows) {
    if (!row.clean_for_analysis) continue;

    const ts = row.stroke_timestamp;
    let ordinalIndex = alignedTimestamps.indexOf(ts);
    if (ordinalIndex < 0) {
      let bestIndex = -1;
      let bestDelta = Infinity;
      alignedTimestamps.forEach((timestamp, candidateIndex) => {
        const delta = Math.abs(timestamp - ts);
        if (delta < bestDelta) {
          bestDelta = delta;
          bestIndex = candidateIndex;
        }
      });
      if (bestIndex >= 0 && bestDelta <= STROKE_TIMESTAMP_MATCH_MS) {
        ordinalIndex = bestIndex;
      }
    }

    if (ordinalIndex < 0) continue;

    const window = windows[ordinalIndex];
    if (window) {
      indices.add(Number(window.stroke_index));
    } else {
      indices.add(ordinalIndex + 1);
    }
  }

  return indices;
}

/**
 * Same relative→absolute adjustment as runHullStabilitySession.alignStrokeTimestamps.
 */
export function alignStrokeMetricTimestamps(
  strokeTimestamps: number[],
  boatMotionMinTimestamp: number | null
): number[] {
  if (!strokeTimestamps.length || boatMotionMinTimestamp == null) {
    return strokeTimestamps;
  }
  const metricsMinTs = Math.min(...strokeTimestamps);
  const looksRelative = metricsMinTs <= 5000;
  const offset = looksRelative ? boatMotionMinTimestamp : 0;
  if (offset === 0) return strokeTimestamps;
  return strokeTimestamps.map((timestamp) => timestamp + offset);
}

/**
 * 1-based stroke indices for gate force / arc, derived from scored stability rows.
 */
export function buildAllowedStrokeIndicesFromStability(
  orderedTimestamps: number[],
  stabilityRows: Array<Pick<StrokeStabilityRecord, 'stroke_timestamp' | 'clean_for_analysis'>>,
  boatMotionMinTimestamp: number | null
): Set<number> {
  const alignedTimestamps = alignStrokeMetricTimestamps(orderedTimestamps, boatMotionMinTimestamp);
  const indices = new Set<number>();

  for (const row of stabilityRows) {
    if (!row.clean_for_analysis) continue;

    let index = alignedTimestamps.indexOf(row.stroke_timestamp);
    if (index < 0) {
      let bestIndex = -1;
      let bestDelta = Infinity;
      alignedTimestamps.forEach((timestamp, candidateIndex) => {
        const delta = Math.abs(timestamp - row.stroke_timestamp);
        if (delta < bestDelta) {
          bestDelta = delta;
          bestIndex = candidateIndex;
        }
      });
      if (bestIndex >= 0 && bestDelta <= 150) {
        index = bestIndex;
      }
    }

    if (index >= 0) {
      indices.add(index + 1);
    }
  }

  return indices;
}

/** Ordered stroke timestamps — matches stroke window generation (includes t=0 header if present). */
export function buildOrderedStrokeTimestamps(
  strokeMetricRows: Record<string, unknown>[]
): number[] {
  const timestamps: number[] = [];
  for (const row of strokeMetricRows) {
    const timestamp = Number(row.timestamp);
    if (typeof timestamp !== 'number' || !Number.isFinite(timestamp) || timestamp < 0) {
      continue;
    }
    timestamps.push(timestamp);
  }
  return timestamps;
}

/**
 * 1-based stroke indices allowed by hull-set filter (matches stroke_windows.stroke_index).
 */
export function buildAllowedStrokeIndices(
  orderedTimestamps: number[],
  allowedTimestamps: Set<number>
): Set<number> {
  const indices = new Set<number>();
  orderedTimestamps.forEach((timestamp, index) => {
    if (allowedTimestamps.has(timestamp)) {
      indices.add(index + 1);
    }
  });
  return indices;
}

export function isStrokeIndexAllowed(
  strokeIndex: number,
  allowedStrokeIndices: Set<number> | null
): boolean {
  if (!allowedStrokeIndices) return true;
  return allowedStrokeIndices.has(strokeIndex);
}
